import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FiagroLiabilityRaw,
  FiagroLiabilitySubclassesData,
} from 'src/types/operations/fiagro';

export function formatCompetence(dateStr: string): string {
  try {
    return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'MM/yyyy', {
      locale: ptBR,
    });
  } catch {
    return 'N/D';
  }
}

export function formatDateShort(dateStr: string): string {
  try {
    return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'MMM yyyy', {
      locale: ptBR,
    });
  } catch {
    return dateStr;
  }
}

export function formatReferenceDate(dateStr: string): string {
  try {
    return format(parse(dateStr, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy', {
      locale: ptBR,
    });
  } catch {
    return dateStr;
  }
}

export function parseScientificToNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Remove currency symbols and spaces, keeping signs and exponent notation.
    const sanitized = trimmed
      .replace(/[R$\s]/g, '')
      .replace(/[^\d,.\-+eE]/g, '');

    // If contains both comma and dot, assume BR format (thousands + decimal comma).
    const normalized =
      sanitized.includes(',') && sanitized.includes('.')
        ? sanitized.replace(/\./g, '').replace(',', '.')
        : sanitized.includes(',')
          ? sanitized.replace(',', '.')
          : sanitized;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function getMostRecentLiabilityValue(mostRecent?: FiagroLiabilityRaw): {
  passivo: number | null;
  patrimonioLiquido: number | null;
} {
  if (!mostRecent) {
    return { passivo: null, patrimonioLiquido: null };
  }

  return {
    passivo:
      parseScientificToNumber(mostRecent.totalPassivo) ??
      parseScientificToNumber(mostRecent.valorPassivo),
    patrimonioLiquido: parseScientificToNumber(mostRecent.patrimonioLiquido),
  };
}

export function getMostRecentLiabilityQuotesBySubclass(
  quotes: FiagroLiabilitySubclassesData[],
): FiagroLiabilitySubclassesData[] {
  const latestBySubclass = quotes.reduce<
    Record<string, FiagroLiabilitySubclassesData>
  >((acc, quote) => {
    const key = `${quote.cnpjClasse}-${quote.nomeSubclasse}`;
    const current = acc[key];

    if (!current || quote.dataReferencia >= current.dataReferencia) {
      acc[key] = quote;
    }

    return acc;
  }, {});

  return Object.values(latestBySubclass);
}

export function toPercentage(value: unknown): string {
  const numericValue = parseScientificToNumber(value);

  if (numericValue === null) {
    return 'N/D';
  }

  return `${(numericValue * 100).toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  })}%`;
}
